import React from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { Fonts, Palette } from '../../components/utils';
import { CONVERSATION_TYPE } from './constants';
import ChatAvatar from './ChatAvatar';

const formatPreviewTime = (iso) => {
  if (!iso) return '';
  const m = moment(iso);
  if (!m.isValid()) return '';
  if (m.isSame(moment(), 'day')) return m.format('h:mm A');
  if (m.isSame(moment().subtract(1, 'day'), 'day')) return 'Yesterday';
  if (m.isSame(moment(), 'year')) return m.format('MMM D');
  return m.format('MMM D, YYYY');
};

const ChatConversationListItem = ({ conversation, onClick }) => {
  const isDirect = Number(conversation.conversation_type) === CONVERSATION_TYPE.DIRECT;

  const name = isDirect
    ? conversation.resident_name || 'Resident'
    : conversation.title || 'Group';

  const unitLabel = isDirect && conversation.unit_num ? `Unit ${conversation.unit_num}` : null;
  const titleLine = unitLabel ? `${name} · ${unitLabel}` : name;
  const unread = Number(conversation.unread_count) || 0;
  const timeLabel = formatPreviewTime(conversation.last_message_at);
  const isUnread = unread > 0;

  return (
    <Row type="button" onClick={() => onClick(conversation)}>
      <ChatAvatar avatar={conversation.resident_avatar} name={name} size={40} />
      <Content>
        <NameTitle title={titleLine}>{titleLine}</NameTitle>
        <Preview title={conversation.last_message_preview || ''}>
          {conversation.last_message_preview || 'No messages yet'}
        </Preview>
      </Content>
      <Meta>
        {timeLabel && <Time $unread={isUnread}>{timeLabel}</Time>}
        {isUnread && <UnreadBadge>{unread > 99 ? '99+' : unread}</UnreadBadge>}
      </Meta>
    </Row>
  );
};

const Row = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;
  box-sizing: border-box;
  width: 100%;
  gap: 12px;
  padding: 12px 20px;
  border: none;
  border-bottom: 0.5px solid ${Palette.SOLITUDE};
  background: transparent;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: ${Palette.GREY_50};
  }

  &:focus {
    outline: none;
    background: ${Palette.GREY_50};
  }
`;

const Content = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
`;

const NameTitle = styled.span`
  color: ${Palette.BLACK};
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  letter-spacing: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Preview = styled.span`
  color: ${Palette.BLACK_50};
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Meta = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
`;

const Time = styled.span`
  color: ${({ $unread }) => ($unread ? Palette.OCEANIC : Palette.BLACK_50)};
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0;
  white-space: nowrap;
`;

const UnreadBadge = styled.span`
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  min-width: 20px;
  padding: 0;
  border-radius: 999px;
  background: ${Palette.HAZY_BLUE};
  color: ${Palette.WHITE};
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 11px;
  font-weight: 600;
  line-height: 20px;
  text-align: center;
`;

export default ChatConversationListItem;
